import { useCallback } from "react";
import { useHubSpotAdapterAxiosInstance } from "./useHubSpotAdapterAxiosInstance.ts";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";

export type AssignmentRule =
  | "under_capacity"
  | "tier1_overflow"
  | "last_resort"
  | "requested";

export type AssignmentKind =
  | "booking"
  | "reschedule"
  | "pending_exit"
  | "reassign";

export type CandidateStatus =
  | "chosen"
  | "ranked_lower"
  | "cap_reached"
  | "busy_google"
  | "busy_scheduler";

export type RankingFactor = "trial" | "cr" | "tier" | "round_robin";

export type TraceCandidateDTO = {
  closerId: string;
  name: string | null;
  status: CandidateStatus;
  rank: number | null;
  lostOn: RankingFactor | null;
  callsOnDay: number | null;
  capacity: number | null;
  cr: number | null;
  tierRank: number | null;
  inTrial: boolean;
};

export type NextAssignmentDTO = {
  kind: AssignmentKind;
  slotStart: string;
  closerId: string;
  closerName: string | null;
};

export type AssignmentStateDTO =
  | { kind: "current" }
  | { kind: "superseded"; next: NextAssignmentDTO }
  | { kind: "cancelled" }
  | { kind: "pending" };

export type AssignmentTraceDTO = {
  bookingId: string;
  kind: AssignmentKind;
  channel: "web" | "setter";
  assignedAt: string;
  slotStart: string;
  slotEnd: string;
  rule: AssignmentRule;
  chosenCloserId: string;
  idealCloserId: string | null;
  overBusy: boolean;
  candidates: TraceCandidateDTO[];
  state: AssignmentStateDTO;
};

export type AssignmentsDTO = {
  fromDay: string;
  toDay: string;
  traces: AssignmentTraceDTO[];
};

export function useGetSchedulerAssignments() {
  const axiosInstance = useHubSpotAdapterAxiosInstance();
  const { withLoading, loading } = useWithLoading<AssignmentsDTO>();

  const getAssignmentsCall = useCallback(
    async ({ fromDay, toDay }: { fromDay: string; toDay: string }) => {
      const { data } = await axiosInstance.get<AssignmentsDTO>(
        `/closer-availability/scheduler/assignments?fromDay=${encodeURIComponent(fromDay)}&toDay=${encodeURIComponent(toDay)}`,
      );

      if (!Array.isArray(data?.traces)) {
        console.error("Unexpected assignments response:", data);
        throw new Error("Unexpected response format from HubSpot adapter");
      }

      return data;
    },
    [axiosInstance],
  );

  const getSchedulerAssignments = useCallback(
    async (range: { fromDay: string; toDay: string }) => {
      return await withLoading(() => getAssignmentsCall(range));
    },
    [withLoading, getAssignmentsCall],
  );

  return {
    getSchedulerAssignments,
    loading,
  };
}
